import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { API } from '../lib/api';
import { useWholesaleAuth } from '../context/WholesaleAuthContext';
import { useWholesaleCart } from '../context/WholesaleCartContext';

const WholesaleOrdersPage = () => {
    const { getAuthHeaders } = useWholesaleAuth();
    const { clearCart } = useWholesaleCart();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const loadOrders = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/wholesale/orders`, { headers: getAuthHeaders() });
            setOrders(response.data);
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const handleReorder = async (orderId) => {
        setBusyId(orderId);
        try {
            clearCart();
            const response = await axios.post(`${API}/wholesale/orders/${orderId}/reorder`, {}, { headers: getAuthHeaders() });
            alert(`Reorder submitted: ${response.data.order_number}`);
            await loadOrders();
        } catch (error) {
            alert(error.response?.data?.detail || 'Failed to reorder');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <section className="pt-24 pb-12 border-b border-black/10">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="text-maple-red font-mono text-sm tracking-widest">WHOLESALE PORTAL</p>
                        <h1 className="font-anton text-5xl text-maple-dark mt-4">ORDER HISTORY</h1>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/wholesale/products" className="border border-black/10 px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-maple-dark hover:text-white transition-colors">Products</Link>
                        <Link to="/wholesale/cart" className="border border-black/10 px-5 py-3 text-sm uppercase tracking-[0.2em] hover:bg-maple-dark hover:text-white transition-colors">Cart</Link>
                    </div>
                </div>
            </section>
            <section className="py-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {loading ? (
                        <div className="py-24 text-center text-maple-dark/60">Loading wholesale orders...</div>
                    ) : orders.length === 0 ? (
                        <div className="text-center py-20 border border-black/10">
                            <p className="text-maple-dark/40 font-mono text-lg mb-6">No wholesale orders yet</p>
                            <Link to="/wholesale/products" className="btn-primary inline-block"><span>Start Ordering</span></Link>
                        </div>
                    ) : (
                        <div className="grid gap-4">
                            {orders.map(order => (
                                <div key={order.id} className="border border-black/10 bg-maple-secondary p-6">
                                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                        <div>
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <h2 className="font-anton text-2xl text-maple-dark">{order.order_number}</h2>
                                                <span className="px-2 py-0.5 text-[10px] uppercase bg-maple-dark text-white">{order.status}</span>
                                            </div>
                                            <p className="text-maple-dark/50 text-sm mt-2">Placed {new Date(order.created_at).toLocaleString()}</p>
                                            <div className="mt-4 space-y-1">
                                                {order.items?.map((item, idx) => (
                                                    <p key={idx} className="text-maple-dark/70 text-sm">{item.quantity} × {item.product_name} — ${Number(item.price).toFixed(2)}</p>
                                                ))}
                                            </div>
                                            {order.notes && <p className="text-maple-dark/55 text-sm mt-4">Notes: {order.notes}</p>}
                                        </div>
                                        <div className="text-right">
                                            <p className="font-mono text-2xl text-maple-dark">${Number(order.total || 0).toFixed(2)}</p>
                                            <button onClick={() => handleReorder(order.id)} disabled={busyId === order.id} className="mt-4 inline-flex items-center gap-2 px-4 py-3 bg-maple-red text-white text-sm uppercase tracking-[0.2em] hover:bg-maple-dark transition-colors disabled:opacity-50">
                                                <RotateCcw className="w-4 h-4" />
                                                <span>{busyId === order.id ? 'Reordering...' : 'Reorder'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default WholesaleOrdersPage;
